import React, { useState, useMemo, useEffect, useContext, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { utcToZonedTime } from 'date-fns-tz';
import _ from 'lodash';

import './NumberBet.scss';
import { RootState } from '../../../configs/store';
import NumberBetContext from './Context/NumberBet.context';
import { Bet } from '../../../interfaces/BetslipState';
import SimpleBet from './Components/SimpleBet';
import ComboBet from './Components/ComboBet';
import SpecialBet from './Components/SpecialBet';
import QuickBet from './Components/QuickBet';
import AppContext from 'src/App.context';
import { generateCalculationsPayload } from '../../../utils/lottoHelper';
import BetHeader from '../SportBet/Components/BetHeader/BetHeader';
import classNames from 'classnames';

interface NumberBetProps {
  index?: number,
  bet: Bet,
  checkCombinable?: boolean;
  stake?: {
    stake?: number;
    maximumProfit?: number;
    profit: number;
    singleStake: number;
  };
  rule?: {
    default: number;
    maxBetslip: number;
    maximum: number;
    minBetslip: number;
    minimum: number;
  };
  allowStakes?: boolean;
  children?: React.ReactNode,
  showHeader?: boolean,
  showTournament?:boolean,
  hideProfit: boolean
}

export default function NumberBet({
  children,
  index,
  bet,
  checkCombinable,
  stake,
  showHeader=true,
  rule,
  showTournament=true,
  hideProfit
} : NumberBetProps) {
  const singleBet = bet;

  const [amount, setAmount] = useState<string>('0');
  const [banker, setBanker] = useState<boolean>(false);
  const [showCombinations, setShowCombinations] = useState(false);
  const [combinations, setCombinations] = useState();
  const [validMessage, setValidMessage] = useState('');

  const { t } = useTranslation();
  const { selectedOddsType, selectedTimezone } = useSelector(
    (state: RootState) => state.odds
  );

  const selectedSlip = useSelector((state: RootState) => state.betslip.slip);
  const { singleSlip, accaSlip, systemSlip, divisor } = useSelector((state: RootState) => state.betslip);
  const dispatch = useDispatch();

  const date = useMemo(
    () => utcToZonedTime(bet.datetime, selectedTimezone),
    [bet.datetime, selectedTimezone]
  );

  const showCombinableWarning =
  checkCombinable &&
  bet.combinationExclusions &&
  bet.combinationExclusions.length > 0;
  const { isPortrait } = useContext(AppContext);

  const isSuspended = +bet.marketStatusNumber === 3 || +bet.outcomeStatusNumber === 3;

  const onAmountChange = async (name: string, value: number) => {
    dispatch.betslip.updateBetStake({ index: index, value: +value });
  };

  useEffect(() => {
    let amount = '0.00';
    if (selectedSlip === 0) {
      let value = singleSlip.stakes[index];
      if (stake && singleSlip.stakeSelection === 'totalStake') {
        value = stake.stake;
      } else if (singleSlip.stakeSelection === 'sameStake') {
        value = singleSlip.sameStake;
      }

      amount = (value / divisor).toString();
    }
    setAmount(amount);
    const stakeValue = singleSlip.stakes[index] / divisor;

    if (rule && rule.minimum > stakeValue) {
      setValidMessage(`${ t('betslip.stakeMinimumError') } ${ rule.minimum }`);
    } else if (rule && rule.maximum < stakeValue) {
      setValidMessage(`${ t('betslip.stakeMaximumError') } ${ rule.maximum }`);
    } else {
      setValidMessage('');
    }
  }, [bet, index, rule, selectedSlip, singleSlip, stake, t]);

  useEffect(() => {
    let _selectedSlip = singleSlip;
    if (selectedSlip === 1) _selectedSlip = accaSlip;
    else if (selectedSlip === 2) _selectedSlip = systemSlip;
    setBanker(!!_selectedSlip.bankers[index]);
  }, [bet, index, rule, selectedSlip, singleSlip, stake]);

  useEffect(() => {
    dispatch.betslip.updateBankerAtIndex({ index: index, isBanker: banker });
  }, [banker]);

  const handleUpdate = useCallback(() => {
    const stakeValue = singleSlip.stakes[index] / divisor;
    const value = rule.minimum > stakeValue ? rule.minimum : rule.maximum;
    dispatch.betslip.updateBetStake({ index, value });
  }, []);

  const changePermutations = async (event) => {
    event.preventDefault();

    const { selectedNumbers } = bet;
    const numbers = _.map(_.filter(selectedNumbers, (number) => number.type !== 'bonus'), (number) => number.number);
    const bankers = _.map(
      _.filter(selectedNumbers, { isBanker: true }),
      (banker) => banker.number
    );
    let filteredBonus;

    if (bet.market?.typeCode === 800){
      filteredBonus = _.filter(selectedNumbers, { type: 'bonus' });
    }

    const payload = generateCalculationsPayload(
      numbers,
      bankers,
      bet.market,
      bet.eventCode,
      amount,
      bet.numbersToPredictCount,
      filteredBonus
    );

    const data = await dispatch.lotto.permutations(payload);

    if (data){
      dispatch.betslip.changeReadBetPermutations({ showPermutations: true, permutations: data.bets[0] });

      setCombinations(data.bets[0]);
      setShowCombinations(true);
    }
  };

  const context = {
    singleBet,
    amount,
    setAmount,
    banker,
    setBanker,
    validMessage,
    date,
    selectedOddsType,
    showCombinableWarning,
    onAmountChange,
    isPortrait,
    handleUpdate,
    changePermutations,
    selectedSlip,
    singleSlip,
    stake,
    rule,
    combinations,
    showCombinations,
    setShowCombinations
  };

  return (
    <NumberBetContext.Provider value={ context }>
      <div className={
        classNames({
          'number-bet-container': true,
          'number-bet-container--no-profit': hideProfit,
          'sport-bet-container--suspended': isSuspended
        })
      }
      >
        {
          isSuspended && (
            <div className="sport-bet-container__suspended-bg" />
          )
        }
        {
          showHeader && <BetHeader
            sport={ bet.sport }
            tournamentName={ `${ bet.tournamentName }, ${ bet.country }` }
            eventName={ bet.eventName }
            showTournament={ showTournament }
            fixtureCode={ bet.fixtureCode }
            betCode={ bet.betCode }
            date={ date }
            venue={ bet.venue }
          />
        }
        { children }
      </div>
    </NumberBetContext.Provider>
  );
}

NumberBet.SimpleBet = SimpleBet;
NumberBet.ComboBet = ComboBet;
NumberBet.SpecialBet = SpecialBet;
NumberBet.QuickBet = QuickBet;
